// REMPLACEMENT COMPLET du fichier : frontend/src/components/dashboard/RecentActivity.jsx
// PARTIE 1 : Imports, états et logique

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import activityService from '../../services/activityService';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate } from '../../utils/formatters';

const RecentActivity = () => {
  const { currentUser } = useAuth();
  
  // États pour les données et la pagination
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    pages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    action: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // État pour afficher/masquer les filtres
  const [showFilters, setShowFilters] = useState(false);

  // Vérifier si l'utilisateur est admin
  const isAdmin = currentUser?.role === 'admin';

  // Fonction pour charger les activités
  const loadActivities = useCallback(async () => {
    // Seuls les admins peuvent voir les activités
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError(null);
      
      // Préparer les paramètres de la requête
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters // Inclure les filtres actifs
      };
      
      const response = await activityService.getActivities(params);
      
      setActivities(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        pages: response.pagination.pages,
        hasNextPage: response.pagination.hasNextPage,
        hasPrevPage: response.pagination.hasPrevPage
      });
      
    } catch (error) {
      console.error('Erreur lors du chargement des activités:', error);
      setError(error.message || 'Erreur lors du chargement des activités');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, pagination.page, pagination.limit, filters]);

  // Charger les activités au montage du composant et quand les dépendances changent
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Fonction pour changer de page
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Fonction pour appliquer les filtres
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fonction pour réinitialiser les filtres
  const handleResetFilters = () => {
    setFilters({
      action: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Fonction pour appliquer les filtres (remettre à la page 1)
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Fonction pour obtenir l'icône en fonction du type d'activité
  const getActivityIcon = (action) => {
    const iconClasses = "h-5 w-5";
    
    switch (action) {
      case 'login':
        return (
          <svg className={`${iconClasses} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        );
      case 'logout':
        return (
          <svg className={`${iconClasses} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        );
      case 'import':
        return (
          <svg className={`${iconClasses} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        );
      case 'modify':
        return (
          <svg className={`${iconClasses} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'comment':
        return (
          <svg className={`${iconClasses} text-purple-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'export':
        return (
          <svg className={`${iconClasses} text-indigo-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'user_create':
      case 'user_update':
      case 'user_delete':
        return (
          <svg className={`${iconClasses} text-pink-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        );
      default:
        return (
          <svg className={`${iconClasses} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Fonction pour obtenir la description de l'activité
  const getActivityDescription = (activity) => {
    const { action, details, userId } = activity;
    const username = userId?.username || 'Utilisateur inconnu';
    const userRole = userId?.role || '';
    
    // Texte de base avec nom d'utilisateur
    const userInfo = `${username}${userRole ? ` (${userRole})` : ''}`;
    
    switch (action) {
      case 'login':
        return `${userInfo} s'est connecté`;
      case 'logout':
        return `${userInfo} s'est déconnecté`;
      case 'import':
        if (details?.filename) {
          return `${userInfo} a importé le fichier "${details.filename}"`;
        }
        return `${userInfo} a importé des données`;
      case 'modify':
        if (details?.contractNumber) {
          return `${userInfo} a modifié le contrat ${details.contractNumber}`;
        }
        return `${userInfo} a modifié un contrat`;
      case 'comment':
        if (details?.contractNumber) {
          return `${userInfo} a commenté le contrat ${details.contractNumber}`;
        }
        return `${userInfo} a ajouté un commentaire`;
      case 'export':
        return `${userInfo} a exporté des données`;
      case 'user_create':
        const createdUsername = details?.username || 'un utilisateur';
        return `${userInfo} a créé l'utilisateur "${createdUsername}"`;
      case 'user_update':
        const updatedUsername = details?.username || 'un utilisateur';
        return `${userInfo} a modifié l'utilisateur "${updatedUsername}"`;
      case 'user_delete':
        const deletedUsername = details?.username || 'un utilisateur';
        return `${userInfo} a supprimé l'utilisateur "${deletedUsername}"`;
      case 'delete_comment':
        return `${userInfo} a supprimé un commentaire`;
      case 'create_scheduled_import':
        return `${userInfo} a créé une importation planifiée`;
      case 'update_scheduled_import':
        return `${userInfo} a modifié une importation planifiée`;
      case 'delete_scheduled_import':
        return `${userInfo} a supprimé une importation planifiée`;
      case 'run_scheduled_import':
        return `${userInfo} a exécuté une importation planifiée`;
      default:
        return `${userInfo} a effectué une action ${action}`;
    }
  };

  // Fonction pour obtenir le badge de rôle utilisateur
  const getRoleBadge = (role) => {
    const badgeClasses = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";
    
    switch (role) {
      case 'admin':
        return <span className={`${badgeClasses} bg-red-100 text-red-800`}>Admin</span>;
      case 'admin_assistant':
        return <span className={`${badgeClasses} bg-blue-100 text-blue-800`}>Assistant</span>;
      case 'visitor':
        return <span className={`${badgeClasses} bg-gray-100 text-gray-800`}>Visiteur</span>;
      default:
        return null;
    }
  };

  // Si l'utilisateur n'est pas admin, ne pas afficher le composant
  if (!isAdmin) {
    return null;
  }
  // REMPLACEMENT COMPLET du fichier : frontend/src/components/dashboard/RecentActivity.jsx
// PARTIE 2 COMPLÈTE : Interface utilisateur et rendu

  return (
    <div className="bg-white shadow rounded-lg">
      {/* En-tête avec titre et bouton filtres */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Activités récentes</h3>
            <p className="text-sm text-gray-600">Historique des actions des utilisateurs</p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Bouton pour afficher/masquer les filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
              </svg>
              Filtres
            </button>
            
            {/* Bouton actualiser */}
            <button
              onClick={loadActivities}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <svg className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Section des filtres (visible conditionnellement) */}
      {showFilters && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Filtre par action */}
            <div>
              <label htmlFor="action-filter" className="block text-sm font-medium text-gray-700">
                Action
              </label>
              <select
                id="action-filter"
                name="action"
                value={filters.action}
                onChange={handleFilterChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Toutes les actions</option>
                {activityService.getAvailableActions().map(action => (
                  <option key={action.value} value={action.value}>
                    {action.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Filtre date de début */}
            <div>
              <label htmlFor="date-from" className="block text-sm font-medium text-gray-700">
                Date de début
              </label>
              <input
                type="date"
                id="date-from"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            {/* Filtre date de fin */}
            <div>
              <label htmlFor="date-to" className="block text-sm font-medium text-gray-700">
                Date de fin
              </label>
              <input
                type="date"
                id="date-to"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            {/* Boutons d'action */}
            <div className="flex items-end space-x-2">
              <button
                onClick={handleApplyFilters}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Appliquer
              </button>
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="px-6 py-4">
        {/* Affichage de l'erreur */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Indicateur de chargement */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner message="Chargement des activités..." />
          </div>
        ) : (
          <>
            {/* Information sur le nombre total d'activités */}
            {pagination.total > 0 && (
              <div className="mb-4 text-sm text-gray-600">
                Affichage de {((pagination.page - 1) * pagination.limit) + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total} activités
              </div>
            )}

            {/* Liste des activités */}
            {activities && activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div key={activity._id} className="flex items-start p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    {/* Icône de l'activité */}
                    <div className="flex-shrink-0 mr-4 mt-1">
                      {getActivityIcon(activity.action)}
                    </div>
                    
                    {/* Contenu de l'activité */}
                    <div className="flex-1 min-w-0">
                      {/* Description de l'activité */}
                      <p className="text-sm text-gray-900 mb-1">
                        {getActivityDescription(activity)}
                      </p>
                      
                      {/* Informations utilisateur et date */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {/* Badge du rôle utilisateur */}
                          {activity.userId?.role && getRoleBadge(activity.userId.role)}
                          
                          {/* Email de l'utilisateur (si disponible) */}
                          {activity.userId?.email && (
                            <span className="text-xs text-gray-500">
                              {activity.userId.email}
                            </span>
                          )}
                          
                          {/* Adresse IP (si disponible) */}
                          {activity.ipAddress && (
                            <span className="text-xs text-gray-400">
                              IP: {activity.ipAddress}
                            </span>
                          )}
                        </div>
                        
                        {/* Date et heure */}
                        <div className="text-xs text-gray-500">
                          {formatDate(activity.timestamp, true)}
                        </div>
                      </div>
                      
                      {/* Détails supplémentaires (si disponibles) */}
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          <details className="cursor-pointer">
                            <summary className="hover:text-indigo-600">
                              Voir les détails
                            </summary>
                            <div className="mt-1 pl-4 border-l-2 border-gray-200">
                              <pre className="whitespace-pre-wrap text-xs">
                                {JSON.stringify(activity.details, null, 2)}
                              </pre>
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Message si aucune activité
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0118 28c2.624 0 4.928 1.018 6.713 2.714" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune activité</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {Object.values(filters).some(f => f) 
                    ? 'Aucune activité trouvée avec ces filtres.'
                    : 'Aucune activité récente disponible.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* PAGINATION COMPLÈTE */}
      {pagination.pages > 1 && !loading && (
        <div className="bg-white border-t border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Pagination mobile */}
            <div className="flex-1 flex justify-between items-center sm:hidden">
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Première page"
                >
                  ⟪
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Préc.
                </button>
              </div>
              
              <span className="text-sm text-gray-700 flex items-center px-2">
                Page {pagination.page} sur {pagination.pages}
              </span>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suiv.
                </button>
                <button
                  onClick={() => handlePageChange(pagination.pages)}
                  disabled={!pagination.hasNextPage}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Dernière page"
                >
                  ⟫
                </button>
              </div>
            </div>
            
            {/* Pagination desktop */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de{' '}
                  <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                  {' '}à {' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>
                  {' '}sur{' '}
                  <span className="font-medium">{pagination.total}</span>
                  {' '}activités
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {/* Bouton Première page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={!pagination.hasPrevPage}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Première page"
                  >
                    <span className="sr-only">Première page</span>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {/* Bouton Précédent */}
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Page précédente"
                  >
                    <span className="sr-only">Précédent</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Pages numérotées */}
                  {[...Array(Math.min(5, pagination.pages))].map((_, index) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = index + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = index + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + index;
                    } else {
                      pageNum = pagination.page - 2 + index;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.page === pageNum
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                        title={`Page ${pageNum}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {/* Bouton Suivant */}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Page suivante"
                  >
                    <span className="sr-only">Suivant</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Bouton Dernière page */}
                  <button
                    onClick={() => handlePageChange(pagination.pages)}
                    disabled={!pagination.hasNextPage}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Dernière page"
                  >
                    <span className="sr-only">Dernière page</span>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;